import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arraySwap, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CustomDelete, Loader, NoItemsYet } from 'components';
import { DefaultDates } from '../report/analyticFragment/fragments/constants';
import { CredentialAlertsCard } from './fragments/credentialAlertsCard';
import { AssignedClients } from './fragments/assignedClients';
import {
    AddWidgetDashboard,
    ClientsOverview,
    ExpiringAuthorizationsCard,
    InsuranceAlertsCard,
    ClientDocumentAlertsCard,
    MyTasks,
    NotificationsAlerts,
    SessionNoteRequests,
    UpcomingAppointments,
    YourProfileDashboard,
} from './fragments';
import { FindLoad, useModal, Images } from 'utils';
import { dashboardActions } from 'store';
import { SortableWidget } from './fragments/sortableWidget';

const DELETE_ACTION_TYPE = 'DELETE_DASHBOARD_USER_KPI';
const ACTION_TYPE = 'GET_DASHBOARD_USER_KPIS';

export const Dashboard = () => {
    const { userKpis } = useSelector((state) => ({
        userKpis: state.dashboard.userKpis,
    }));
    const dispatch = useDispatch();
    const location = useLocation();
    const { open } = useModal();
    const loader = FindLoad(ACTION_TYPE);
    const [order, setOrder] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const info = location?.state;
    const { allDates } = DefaultDates(info);

    useEffect(() => {
        dispatch(dashboardActions.getUserKpis(allDates));
    }, [info]);

    useEffect(() => {
        setOrder(userKpis || []);
    }, [userKpis]);

    const handleDragStart = ({ active }) => {
        setActiveId(String(active?.id));
    };

    const handleDragEnd = ({ active, over }) => {
        const clearActive = () => setTimeout(() => setActiveId(null), 250);
        if (!over || active.id === over.id) {
            clearActive();
            return;
        }
        const from = order.findIndex((k) => String(k?.id) === String(active.id));
        const to = order.findIndex((k) => String(k?.id) === String(over.id));
        if (from === -1 || to === -1) {
            clearActive();
            return;
        }
        const next = arraySwap(order, from, to);
        setOrder(next);
        dispatch(dashboardActions.reorderKpis(next, allDates));
        clearActive();
    };

    const handleDeleteWidget = (e, item, afterSuccess) => {
        e.stopPropagation();

        open(
            <CustomDelete
                info={`Remove ${item?.name} Widget!`}
                text={`Are you sure you want to remove this ${item?.name} widget? You can add it back anytime.`}
                handleDel={() => dispatch(dashboardActions.deleteUserKpi(item?.id))}
                afterSuccess={afterSuccess}
                innerText={'Remove'}
                actionType={DELETE_ACTION_TYPE}
            />
        );
    };

    const renderWidget = (kpi) => {
        switch (kpi?.id) {
            case 1:
                return <UpcomingAppointments item={kpi} handleDeleteWidget={handleDeleteWidget} />;
            case 2:
                return <ClientsOverview item={kpi} handleDeleteWidget={(e) => handleDeleteWidget(e, kpi)} />;
            case 3:
                return <CredentialAlertsCard item={kpi} handleDeleteWidget={(e) => handleDeleteWidget(e, kpi)} />;
            case 4:
                return <NotificationsAlerts handleDeleteWidget={(e) => handleDeleteWidget(e, kpi)} />;
            case 5:
                return <AssignedClients item={kpi} handleDeleteWidget={(e) => handleDeleteWidget(e, kpi)} />;
            case 6:
                return <YourProfileDashboard handleDeleteWidget={(e) => handleDeleteWidget(e, kpi)} />;
            case 8:
                return <MyTasks item={kpi} handleDeleteWidget={(e) => handleDeleteWidget(e, kpi)} />;
            case 10:
                return <ExpiringAuthorizationsCard item={kpi} handleDeleteWidget={(e) => handleDeleteWidget(e, kpi)} />;
            case 11:
                return <InsuranceAlertsCard item={kpi} handleDeleteWidget={(e) => handleDeleteWidget(e, kpi)} />;
            case 12:
                return <SessionNoteRequests item={kpi} handleDeleteWidget={(e) => handleDeleteWidget(e, kpi)} />;
            case 13:
                return <ClientDocumentAlertsCard item={kpi} handleDeleteWidget={(e) => handleDeleteWidget(e, kpi)} />;
            default:
                return null;
        }
    };

    return (
        <div className="dashboard-page-wrapper">
            <div className="dashboard-page-header-wrapper">
                <AddWidgetDashboard />
            </div>

            {loader?.length ? (
                <Loader height={'70vh'} />
            ) : !userKpis?.length ? (
                <NoItemsYet
                    text="Your dashboard is empty"
                    subTitle="Add widgets to start tracking clients, appointments, and alerts"
                    image={Images.noDashboard}
                    height={'82vh'}
                />
            ) : (
                <DndContext
                    sensors={sensors}
                    autoScroll={false}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={() => setActiveId(null)}
                >
                    <SortableContext items={order.map((k) => String(k?.id))} strategy={() => null}>
                        <div className="dashboard-widgets-grid">
                            {order.map((kpi) => (
                                <SortableWidget key={String(kpi?.id)} id={String(kpi?.id)} isActive={String(kpi?.id) === String(activeId)}>
                                    {renderWidget(kpi)}
                                </SortableWidget>
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}
        </div>
    );
};
